import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MDIconComponent, MDTagComponent } from '@melodic-ui/components';

/**
 * Classes of resource-level failure surfaced to the user when an id (or combination of ids)
 * in the URL points somewhere it shouldn't.
 *
 * - `not-found`  — the resource doesn't exist (HTTP 404).
 * - `forbidden`  — the resource exists but this user can't see it (HTTP 403).
 * - `invalid`    — the id is malformed, or a combination of ids is inconsistent
 *                  (e.g. a lesson that belongs to a different enrollment than the URL claims).
 * - `error`      — any other failure while loading the resource.
 */
export type ResourceErrorType = 'not-found' | 'forbidden' | 'invalid' | 'error';

interface ResourceErrorContent {
	tag: string;
	icon: string;
	title: string;
	message: string;
}

/**
 * Consistent, friendly inline panel for resource-level routing problems.
 *
 * Rendered inside a feature shell (keeping breadcrumbs / nav / back button) rather than
 * redirecting to a full-page error, so the user keeps context and can recover in one click.
 * Shared across all three apps via `@lb/components`.
 */
@Component({
	selector: 'lb-resource-error',
	standalone: true,
	imports: [RouterLink, MDIconComponent, MDTagComponent],
	templateUrl: './resource-error.component.html',
	styleUrl: './resource-error.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceErrorComponent {
	/** Which class of failure occurred. Drives the icon, heading, and copy. */
	public readonly type = input<ResourceErrorType>('error');

	/** Human-friendly noun for the missing resource, e.g. 'enrollment' or 'lesson'. */
	public readonly resourceLabel = input<string>('item');

	/** Optional in-app route for the primary recovery action. Falls back to browser back. */
	public readonly backLink = input<string | null>(null);

	/** Label for the primary recovery action. */
	public readonly backLabel = input<string>('Go Back');

	protected readonly content = computed<ResourceErrorContent>(() => {
		const label = this.resourceLabel();

		switch (this.type()) {
			case 'not-found':
				return {
					tag: 'Not Found',
					icon: 'untitled-ui/general/search-md',
					title: `We couldn't find that ${label}`,
					message: 'It may have been removed, or the link you followed may be out of date.'
				};
			case 'forbidden':
				return {
					tag: 'No Access',
					icon: 'untitled-ui/security/lock-01',
					title: `You don't have access to this ${label}`,
					message: 'If you believe this is a mistake, please contact your administrator.'
				};
			case 'invalid':
				return {
					tag: 'Invalid Link',
					icon: 'untitled-ui/alerts/alert-triangle',
					title: `This ${label} link doesn't look right`,
					message: `The address is malformed or the IDs don't match. Try opening the ${label} from the list instead.`
				};
			default:
				return {
					tag: 'Error',
					icon: 'untitled-ui/alerts/alert-circle',
					title: `Something went wrong loading this ${label}`,
					message: 'Please try again in a moment.'
				};
		}
	});

	protected goBack(): void {
		window.history.back();
	}
}
